import { z } from 'zod';
/**
 * DOOR ARCHITRAVE, PRICED PER DOOR.
 *
 * Jacky, 1 October 2026: *"instead of using LM, let's use a average for a door (Single or both side)
 * average door height is around 2meters, 800 wide. roughly 5LM used for single sided. 10LM for dual
 * sided. use the Bunning pricing + $75 for cutting & installation"* — and, the same hour, *"also
 * include the 40mm width, it is common"* and *"some people prefer thinner 10-11mm version"*.
 *
 * Then, on the first table: *"$40 single side, $75 for both side. and make it even number to 5's"*, and
 * *"do a 30% markup top of bunning price, and remeber wastage, for single door, need 3 pcs of 2.7M -
 * double side need 6 pc of 2.7M. this way, we can also do customer supply, Artemis install only"*.
 *
 * So the price of a door is arithmetic over four things he can change in one place:
 *
 *   · the DOOR — whole boards a side or both sides use, cuts and waste in the count (3 and 6 of 2.7 m);
 *   · the MARKUP on the shelf price (30%);
 *   · the BOARDS — Bunnings' shelf price for the preferred product (Porta primed finger-jointed pine,
 *     2.7 m, the length the purchasing rule prefers), by width and thickness. The shelf price is the
 *     same for every profile of a size, which is why the list is by size, not by shape;
 *   · the LABOUR — cutting and fitting: one figure for one side of the door, one for both.
 *
 * The figure a customer reads is rounded to the nearest `round_to_aud` (his "even number to 5's").
 *
 * Shelf prices are GST-inclusive retail; a customer figure is EX GST with "+ GST" in words (the amount
 * rule), so the material is divided by 1.1 before the markup and the labour. Counting pieces rather than
 * metres is what lets the same document serve a customer who buys the boards: the fitting alone. A size with no board on the list
 * is "quoted on request", never guessed. `checked_on` is when the shelf was last read.
 */
export const ArchitraveBoard = z.object({
    width_mm: z.number().int().positive().max(500),
    thickness_mm: z.number().int().positive().max(100),
    product: z.string().trim().min(1).max(160),
    length_m: z.number().positive().max(20),
    price_inc_gst: z.number().nonnegative().max(10000),
});
export const ArchitravePricingSchema = z.object({
    source: z.string().trim().min(1).max(200),
    checked_on: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** The door the pieces are counted for, and how many whole boards a side or both sides use — cuts
     *  and waste included (Jacky, 1 October: *"for single door, need 3 pcs of 2.7M - double side need 6 pc"*). */
    door: z.object({ height_mm: z.number().int().positive().max(5000), width_mm: z.number().int().positive().max(3000), pieces_single: z.number().int().positive().max(50), pieces_dual: z.number().int().positive().max(100) }),
    /** On top of the shelf price of the boards: 0.3 = 30%. */
    markup: z.number().min(0).max(5),
    /** Cutting and fitting, GST exclusive, per door: one side, or both sides. Stands alone when the customer supplies the boards. */
    labour_aud: z.object({ single: z.number().nonnegative().max(10000), dual: z.number().nonnegative().max(10000) }),
    /** The customer figure is rounded to the nearest multiple of this. 0 = exact cents. */
    round_to_aud: z.number().nonnegative().max(100).default(5),
    boards: z.array(ArchitraveBoard).max(40),
});
export const architravePricingDefaults = {
    source: 'Bunnings shelf price — Porta primed FJ pine, 2.7 m lengths (the preferred product and length of the purchasing rule)',
    checked_on: '2026-10-01',
    door: { height_mm: 2000, width_mm: 800, pieces_single: 3, pieces_dual: 6 },
    markup: 0.30,
    labour_aud: { single: 40, dual: 75 },
    round_to_aud: 5,
    boards: [
        { width_mm: 42, thickness_mm: 18, product: 'Porta 42 × 18 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 10.20 },
        { width_mm: 66, thickness_mm: 18, product: 'Porta 66 × 18 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 16.38 },
        { width_mm: 92, thickness_mm: 18, product: 'Porta 92 × 18 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 23.48 },
        { width_mm: 138, thickness_mm: 18, product: 'Porta 138 × 18 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 30.90 },
        { width_mm: 42, thickness_mm: 11, product: 'Porta 42 × 11 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 9.17 },
        { width_mm: 66, thickness_mm: 11, product: 'Porta 66 × 11 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 9.99 },
        { width_mm: 92, thickness_mm: 11, product: 'Porta 92 × 11 mm 2.7 m primed FJ pine architrave & skirting', length_m: 2.7, price_inc_gst: 18.54 },
    ],
};
const r2 = (n) => Math.round(n * 100) / 100;
/** The board on the list for a catalogue size (a nominal 66–67 or 138–140 mm board is matched within
 *  2 mm), or null when there is none — and then the size is quoted on request. */
export function architraveBoardFor(cfg, size) {
    return cfg.boards.find(b => b.thickness_mm === size.thickness_mm && b.width_mm >= size.width_min_mm - 2 && b.width_mm <= size.width_max_mm + 2) ?? null;
}
/** What one door costs the customer, EX GST: whole boards at shelf price (GST taken out) plus the
 *  markup, three for one side and six for both — cuts and waste are in the count — plus cutting and
 *  fitting, rounded to his 5s. And the fitting alone, for the customer who buys the boards. */
export function architraveDoorPrice(cfg, size) {
    const board = architraveBoardFor(cfg, size);
    if (!board)
        return null;
    const round = (n) => cfg.round_to_aud > 0 ? Math.round(n / cfg.round_to_aud) * cfg.round_to_aud : r2(n);
    const piece = board.price_inc_gst / 1.1 * (1 + cfg.markup);
    return { board, piece_ex_gst: r2(piece), pieces_single: cfg.door.pieces_single, pieces_dual: cfg.door.pieces_dual,
        single_ex_gst: round(piece * cfg.door.pieces_single + cfg.labour_aud.single), dual_ex_gst: round(piece * cfg.door.pieces_dual + cfg.labour_aud.dual),
        fit_single_ex_gst: round(cfg.labour_aud.single), fit_dual_ex_gst: round(cfg.labour_aud.dual) };
}
//# sourceMappingURL=architrave-pricing.js.map